import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

/**
 * Keyboard contract for a real tablist (ARIA APG) — one implementation, used
 * by the bottom nav, the screen sub-tabs and the Thread's filter chips (UX
 * item 6 / sol review H8).
 *
 * A `role="tab"` element is only correct alongside two things the app was
 * missing: a roving tabindex (ONE tab in the tab order, arrows move between
 * the rest) and arrow-key navigation. Without them a screen-reader or keyboard
 * user gets a row of tabs that behaves like nothing in particular.
 *
 * The caller passes the key back through `onSelect`; the helper only moves
 * focus and reports WHICH tab was chosen, so a caller whose array index does
 * not match DOM order (disabled chips are skipped) still resolves correctly —
 * every tab carries `data-tab`.
 */
export function tabListKeyDown(
  e: ReactKeyboardEvent<HTMLElement>,
  onSelect: (tab: HTMLElement) => void,
): void {
  const list = e.currentTarget.closest<HTMLElement>('[role="tablist"]');
  if (!list) return;
  const tabs = Array.from(list.querySelectorAll<HTMLElement>('[role="tab"]:not([disabled])'));
  if (tabs.length === 0) return;

  const current = tabs.indexOf(document.activeElement as HTMLElement);
  let next: number;
  switch (e.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      next = (current + 1) % tabs.length;
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      next = (current - 1 + tabs.length) % tabs.length;
      break;
    case 'Home':
      next = 0;
      break;
    case 'End':
      next = tabs.length - 1;
      break;
    default:
      return;
  }

  e.preventDefault();
  const target = tabs[next];
  if (!target) return;
  target.focus();
  onSelect(target);
}
